import { ERC20_ABI } from '@/features/fundraise-block/model/const'
import { viewContractData } from '@/features/fundraise-block/ui/use-fundraise'
import { contractRead, outContractRead } from '@/shared/lib'
import useNotifier from '@/shared/lib/use-notifier'
import { LangStore } from '@/shared/model/store'
import { ContractsStore } from '@/shared/model/store/contracts-store'
import { Button, Input, Label } from '@/shared/ui'
import { AmountBlock, getIcon } from '@/shared/ui/amount-block/amount-block'
import { X } from 'lucide-react'
import { useEffect, useState } from 'react'

type Props = {
	npo_address?: `0x${string}`
}

export type TAvailableTokens = {
	name: string
	symbol: string
	decimals: number
	address: `0x${string}`
	balance?: any
}

const TokensNpo = ({ npo_address }: Props) => {
	const [tokens, setTokens] = useState<TAvailableTokens[]>([])
	const { langPack } = LangStore()
	const [newTokenName, setNewTokenName] = useState<string>('')
	const { writeContract } = useNotifier()
	const { contracts } = ContractsStore()

	const getSupportedTokens = async () => {
		const result: `0x${string}`[] = await contractRead('SavvaNPO', 'getSupportedTokens', [], npo_address)
		const rl: TAvailableTokens[] = []
		for (let i = 0; i < result.length; i++) {
			const res = await viewContractData(result[i])
			const balance = await outContractRead(result[i], ERC20_ABI, 'balanceOf', [npo_address])
			rl.push({ ...res, address: result[i], balance })
		}
		setTokens(rl)
	}

	const addToken = () => {
		writeContract({
			abi: contracts.SavvaNPO.abi,
			address: npo_address || '0x0000000000000000000000000000000000000000',
			functionName: 'addSupportedToken',
			args: [newTokenName]
		})
	}

	const removeToken = (token: `0x${string}`) => {
		writeContract({
			abi: contracts.SavvaNPO.abi,
			address: npo_address || '0x0000000000000000000000000000000000000000',
			functionName: 'removeSupportedToken',
			args: [token]
		})
	}

	useEffect(() => {
		if (!npo_address) return
		getSupportedTokens()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [npo_address])

	return (
		<div>
			<div className='rounded-lg border-[1px] p-4 shadow'>
				<Label>{langPack('npo.addButton')}</Label>
				<div className='flex items-center gap-4'>
					<Input value={newTokenName} onChange={e => setNewTokenName(e.target.value)} placeholder='Token name' />
					<Button onClick={addToken}>{langPack('action.add')}</Button>
				</div>
			</div>
			<div className='mt-5 rounded-lg border-[1px] p-4 shadow'>
				<Label className='mb-2 flex w-full border-b-[1px] border-[#d1d5db] pb-2'>
					{langPack('npo.availableTitle')}
				</Label>
				{tokens.length ? (
					tokens.map((el: TAvailableTokens) => (
						<div key={el.address} className='flex items-center justify-between'>
							<div className='flex items-center gap-4'>
								<div className='scale-[1.5]'>{getIcon(el.symbol)}</div>
								<div className='flex flex-col items-start'>
									<div className='text-[32px] font-bold'>{el.symbol}</div>
									<div className='-mt-3 text-[12px] text-[grey]'>{el.name}</div>
								</div>
							</div>
							<div className='text-[12px] text-[grey]'>{el.address}</div>
							<AmountBlock amount={el.balance} token={el.symbol} size='md' />
							<div className='flex gap-4'>
								<Button size={'icon'} variant={'destructive'} onClick={() => removeToken(el.address)}>
									<X />
								</Button>
							</div>
						</div>
					))
				) : (
					<div className='text-[12px] text-[grey]'>{langPack('npo.emptyState')}</div>
				)}
			</div>
		</div>
	)
}

export default TokensNpo
